import { useState, useEffect, useCallback } from 'react';
import api from './api';

// Людські підписи для машинних action-кодів з backend/utils/activityLog.js.
// Невідомий код (майбутня дія, яку ще не додали сюди) просто показується як є.
const ACTION_LABELS = {
  'auth.login':                      '🔑 Logged in',
  'project.create':                  '📁 Created project',
  'project.update':                  '✎ Updated project',
  'project.delete':                  '🗑 Deleted project',
  'project.status_change':           '🔄 Changed project status',
  'project.archive':                 '📦 Archived project',
  'project.restore':                 '📤 Restored project',
  'document.upload':                 '⬆ Uploaded document',
  'document.delete':                 '🗑 Deleted document',
  'document.folder_create':          '📁 Created folder',
  'document.folder_rename':          '✎ Renamed folder',
  'document.folder_delete':          '🗑 Deleted folder',
  'document.admin_edit_crew_folder': '✎ Updated freelancer\'s personal folder',
  'document.flag':                   '🚩 Flagged document',
  'document.unflag':                 '✓ Unflagged document',
  'assignment.confirm':              '✓ Confirmed readiness',
  'assignment.decline':              '✕ Declined project',
  'freelancer.create':               '👤 Created freelancer',
  'freelancer.update':               '✎ Updated freelancer',
  'freelancer.delete':               '🗑 Deleted freelancer',
  'freelancer.status_change':        '🔄 Changed freelancer status',
  'freelancer.remind':               '✉ Sent reminder',
  'freelancer.remind_all':           '✉ Sent bulk reminder',
  'freelancer.profile_update':       '✎ Updated own profile',
  'freelancer.onboarding_complete':  '🎉 Completed onboarding',
  'user.approve':                    '✓ Approved registration',
  'user.reject':                     '✕ Rejected registration',
  'user.delete':                     '🗑 Deleted user',
};

function actionLabel(action) {
  return ACTION_LABELS[action] || action;
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Компактний рядок з details JSONB — без спроби відтворити повну UI-логіку,
// просто читабельний dump ключ: значення для найпоширеніших полів.
function detailsSummary(details) {
  if (!details || typeof details !== 'object') return null;
  const parts = [];
  if (details.from && details.to) parts.push(`${details.from} → ${details.to}`);
  if (details.status) parts.push(details.status);
  if (details.reason) parts.push(`reason: ${details.reason}`);
  if (details.category) parts.push(details.category);
  if (details.area) parts.push(details.area);
  if (Array.isArray(details.fileNames) && details.fileNames.length) parts.push(details.fileNames.join(', '));
  if (details.oldName) parts.push(`was: ${details.oldName}`);
  return parts.length ? parts.join(' · ') : null;
}

const PAGE_SIZE = 50;

// ─── ActivityLogPage ────────────────────────────────────────────────────────
// Повний журнал дій — окрема сторінка (Admin/SuperAdmin only, бекенд і так
// гейтить requireRole('Admin'), тут просто немає посилання для інших ролей).
export default function ActivityLogPage({ onBack }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal]     = useState(0);
  const [offset, setOffset]   = useState(0);
  const [action, setAction]   = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset };
    if (action) params.action = action;
    return api.activityLog.list(params)
      .then(res => { setEntries(res.entries || []); setTotal(res.total || 0); })
      .catch(err => setError(err.message || 'Failed to load activity log'))
      .finally(() => setLoading(false));
  }, [offset, action]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back
          </button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>ACTIVITY LOG</span>
          <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{total} total</span>
        </div>
        <select value={action} onChange={e => { setAction(e.target.value); setOffset(0); }}
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', padding: '6px 10px', fontSize: '12px' }}>
          <option value="">All actions</option>
          {Object.keys(ACTION_LABELS).map(a => (
            <option key={a} value={a}>{actionLabel(a)}</option>
          ))}
        </select>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: '1100px', margin: '0 auto' }}>
        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px' }}>
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</p>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-dim)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📜</div>
            <p style={{ fontSize: '14px' }}>No activity recorded yet.</p>
          </div>
        ) : (
          <>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
              {entries.map((e, i) => {
                const summary = detailsSummary(e.details);
                return (
                  <div key={e.id}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '12px 18px', borderBottom: i < entries.length - 1 ? '1px solid var(--bg-subtle)' : 'none' }}>
                    <span style={{ color: 'var(--text-disabled)', fontSize: '12px', width: '150px', flexShrink: 0 }}>{formatTime(e.createdAt)}</span>
                    <span style={{ color: 'var(--text-body)', fontSize: '13px', width: '160px', flexShrink: 0 }}>
                      {e.actorName || 'System'}
                      {e.actorRole && <span style={{ color: 'var(--text-disabled)', fontSize: '11px' }}> ({e.actorRole})</span>}
                    </span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '13px', width: '260px', flexShrink: 0 }}>{actionLabel(e.action)}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '13px', flex: 1 }}>
                      {e.targetLabel || '—'}
                      {summary && <span style={{ color: 'var(--text-disabled)' }}> — {summary}</span>}
                    </span>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
              <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: offset === 0 ? 'default' : 'pointer', fontSize: '13px', opacity: offset === 0 ? 0.5 : 1 }}>
                ← Newer
              </button>
              <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(o => o + PAGE_SIZE)}
                style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: offset + PAGE_SIZE >= total ? 'default' : 'pointer', fontSize: '13px', opacity: offset + PAGE_SIZE >= total ? 0.5 : 1 }}>
                Older →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { actionLabel, formatTime };
